import { Banner } from '@sunwebgroup/uds-ui/banner'
import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { RadioButtonField } from '@sunwebgroup/uds-ui/fields/radio-button-field'
import { RadioButtonGroup } from '@sunwebgroup/uds-ui/radio-button'
import * as React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from '@/intl'
import { createGroupsEvent } from '@/scopes/checkout/domain/analytics'
import { FlexContent } from '@/scopes/checkout/groups/components/services/RoomSelection/FlexContent'
import { isAnyRoomTypeAvailable } from '@/scopes/checkout/groups/components/services/RoomSelection/selectors'
import {
  getErrorMessage,
} from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/FormFields/getErrorMessages'
import { useAvailableRoomsQuery } from '@/scopes/checkout/groups/domain/hooks'
import {
  roomSelectionTypeRadioGroupOptions,
  RoomSelectionTypeRadioGroupOptions,
} from '@/scopes/checkout/groups/domain/types/availableRooms'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

export function AssignationMethodFormField() {
  const { data } = useAvailableRoomsQuery()
  const { roomTypes } = data || {}
  const isAnyRoomTypeAvailableToCreateANamedRoom = isAnyRoomTypeAvailable(roomTypes)
  const { setValue, formState, control } = useFormContext()
  const intl = useIntl()
  const { errors } = formState
  const { sendEvent } = useAnalytics()

  const { field } = useController({
    name: 'assignationMethod',
    control,
    rules: {
      required: true,
    },
  })

  const handleRoomSelectionAssignationMethodChange = (value: RoomSelectionTypeRadioGroupOptions) => {
    sendEvent(
      createGroupsEvent({
        description: `RoomSelection`,
        element: 'radio button',
        type: 'selection',
        result: `room assignation method selected: ${value}`,
      }),
    )

    field.onChange(value)

    // Reset other fields when changing assignation method to avoid inconsistent states
    // (e.g. having a room name selected but then switching to random assignation method)
    if (value === 'random') {
      setValue('selectedRoomId', null)
      setValue('newRoomName', null)
      setValue('roomType', null)
      setValue('pinCode', null)
      setValue('isNewRoom', null)
    }
  }

  const errorMessage = getErrorMessage(
    'assignationMethod',
    errors,
    { requiredErrorMessage: intl.formatMessage({ id: 'groups_checkout_steps_room_selection_assignation_method_required_error_message' }) },
  )

  return (
    <FormField feedbackText={errorMessage} status={errors.assignationMethod ? 'error' : 'default'}>
      <RadioButtonGroup
        value={field.value}
        onValueChange={handleRoomSelectionAssignationMethodChange}
        onBlur={field.onBlur}
      >
        <FlexContent gap="primitives-space-12">
          {roomSelectionTypeRadioGroupOptions.map(option => (
            <RadioButtonField
              key={option}
              label={
                option === 'specific'
                  ? intl.formatMessage({
                      id: 'groups_checkout_steps_room_selection_assignation_method_specific_room_label',
                    })
                  : intl.formatMessage({ id: 'groups_checkout_steps_room_selection_assignation_method_random_label' })
              }
              variant="outlined"
              value={option}
              disabled={option === 'random' && !isAnyRoomTypeAvailableToCreateANamedRoom}
            />
          ))}

          {!isAnyRoomTypeAvailableToCreateANamedRoom && (
            <Banner color="warning" size="M">
              <Banner.ContentWrapper>
                <Banner.TextsWrapper>
                  <Banner.Text>{intl.formatMessage({ id: 'groups_checkout_steps_room_selection_assignation_method_no_room_types_available_banner_title' })}</Banner.Text>
                  <Banner.SupportingText>
                    {intl.formatMessage({ id: 'groups_checkout_steps_room_selection_assignation_method_no_room_types_available_banner_description' })}
                  </Banner.SupportingText>
                </Banner.TextsWrapper>
              </Banner.ContentWrapper>
            </Banner>
          )}
        </FlexContent>
      </RadioButtonGroup>
    </FormField>
  )
}
